import StartCardIndicator from "@Features/timeTableSelectedEvent/resultList/_components/StartCardIndicator";
import teamStandings from "@Features/timeTableSelectedEvent/teamStandings/index";
import {
  getCompetitorCount,
  getCountryFlagImage,
  getFlagOrClubImage,
  getPlaceCssClass,
  getUnofficialPlaceCssClass
} from "@Utils/ui";
import React from 'react';
import { useAppSelector, useAppDispatch } from '@App/hooks';
import { useStyles } from './TeamStandingsTabContent.styles';
import { ITimeTableItem, ITimeTableItemState, EventState } from '@App/types';

import {
  selectIsFetching,
  selectError,
  selectData,
  selectDataLoaded,
  selectChangeTimestamp,
  getTeamStandingsDataAsync,
} from './teamStandingsSlice';
import { frontendSettingsApi } from '@App/services/settings/frontendSettingsApi';

import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

import PollingContainer from '@Components/pollingContainer';
import { PollingPromise } from '@Components/pollingContainer/PollingContainer';
import SimpleBar from 'simplebar-react';
import { CircularProgress } from '@material-ui/core';

import clsx from 'clsx';


type TeamStandingsTabContentProps = {
  selectedEvent: ITimeTableItem,
  selectedEventState: ITimeTableItemState | null,
}

type PollTimeoutTickHandler = () => PollingPromise;

function TeamStandingsTabContent({ selectedEvent, selectedEventState }: TeamStandingsTabContentProps) {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const scrollableNodeRef = React.createRef<any>();

  const { data: frontendSettings } = frontendSettingsApi.endpoints.getFrontendSettings.useQueryState('');

  const isFetching = useAppSelector(selectIsFetching);
  const error = useAppSelector(selectError);
  const dataLoaded = useAppSelector(selectDataLoaded);
  const teamStandingsData = useAppSelector(selectData);
  const changeTimeStamp = useAppSelector(selectChangeTimestamp);

  const showNoData = dataLoaded && (selectedEventState?.state === EventState.Scheduled || !teamStandingsData || !teamStandingsData.teams || teamStandingsData.teams.length === 0);

  const handlePollTimeoutTick: PollTimeoutTickHandler = () => {
    return dispatch(getTeamStandingsDataAsync({ timeTableId: selectedEvent.id}));
  };

  if (!frontendSettings) {
    return null;
  }

  return (
    <div className={classes.root}>
      <PollingContainer
        argDeps={selectedEvent.id}
        changeTimeStamp={changeTimeStamp}
        interval={frontendSettings.resultsRefreshInterval}
        onPollTimeoutTick={handlePollTimeoutTick}
      />
      {(isFetching || error) && <CircularProgress className={clsx(classes.progress, dataLoaded && 'data-loaded')} size={dataLoaded ? 20 : 60} />}
      <div id="teamStandingsScrollContainer" className={clsx(classes.scrollContainer, !!teamStandingsData?.note && 'has-note')}>
        <SimpleBar autoHide={false} scrollbarMinSize={125} className={classes.simpleBar} scrollableNodeProps={{ ref: scrollableNodeRef }}>
          {showNoData ? (
            <div>
              <h2 className={classes.noStandings}>There are no team standings data yet, please check again later after the first competitor has finished...</h2>
            </div>
            ) : (
            <table className={classes.startListTable}>
              <colgroup>
                <col style={{ width: '65px' } /* Place */} />
                <col style={{ width: '500px' } /* Athlete */} />
                <col style={{ width: '80px' } /* Result */} />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th className="text-left">Team</th>
                  <th className="text-right">Points</th>
                  <th className="text-right"></th>
                </tr>
              </thead>
              <tbody>
              {teamStandingsData && teamStandingsData.teams.map(team => (
                <tr key={team.teamParticipantId}>
                  <td className={clsx('text-center', 'col-lane',  getPlaceCssClass(team.place))}><span>{team.place}</span></td>
                  <td className="text-left col-athlete">
                    <div className="athlete-flag-container">
                      <div className="flag-container">
                        <div className="flag" style={{backgroundImage: `url('${getCountryFlagImage(team.team)}')`}} />
                        <div className="flag" style={{backgroundImage: `url('${getFlagOrClubImage(team.team, '')}')`}} />
                      </div>
                      <div className={clsx('athlete-container')}>
                        <div className="athlete-name">
                          <span className="team-full-name">{team.teamFull}</span> <span className="team-name">({team.team})</span>
                          {team.competitors &&
                          <ul className="relay-members">
                            {team.competitors.map(member => (
                              <li key={member.sort}><span className="member-order">{member.place}.</span> <span className="member-last-name">{member.lastName?.toLocaleUpperCase()}</span> <span className="member-first-name">{member.firstName}</span> - <span className="result">{member.result}</span></li>
                            ))}
                          </ul>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right col-result">
                    <span className="result">{team.points}</span>
                  </td>
                  <td>&nbsp;</td>
                </tr>
              ))}
              </tbody>
            </table>
          )}
        </SimpleBar>
      </div>
      {teamStandingsData && teamStandingsData.note && <div className={classes.standingsNoteContainer}>
        {teamStandingsData.noteHeader && <><span className="header">{teamStandingsData.noteHeader}</span> - </>} {teamStandingsData.note}
      </div>}
    </div>
  );
}

export default TeamStandingsTabContent;
